import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Screen } from '@/components/Screen'
import { Button } from '@/components/Button'
import { Field, Select, TextInput } from '@/components/Field'
import { BottomSheet } from '@/components/BottomSheet'
import { ListRow } from '@/components/ListRow'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import { PACK_COLORS } from '@/design-system/tokens'
import { PlusIcon, SchoolIcon } from '@/design-system/icons'
import { relativeTime } from '@/lib/format'

/**
 * Documents — family-wide file store.
 * Insurance cards, immunization records, birth certificates, prescriptions, whatever else
 * matters. Files upload to Supabase Storage bucket "documents" under prefix `<family_id>/`
 * so per-family RLS enforces isolation.
 */

type DocumentType = 'insurance' | 'immunization' | 'birth_certificate' | 'prescription' | 'medical_record' | 'legal' | 'other'

const DOC_TYPES: { key: DocumentType; label: string }[] = [
  { key: 'insurance', label: 'Insurance' },
  { key: 'immunization', label: 'Immunization' },
  { key: 'birth_certificate', label: 'Birth certificate' },
  { key: 'prescription', label: 'Prescription' },
  { key: 'medical_record', label: 'Medical record' },
  { key: 'legal', label: 'Legal' },
  { key: 'other', label: 'Other' },
]

interface DocumentRow {
  id: string
  family_id: string
  person_id: string | null
  document_type: string
  display_name: string
  storage_status: string
  storage_ref: string | null
  mime_type: string | null
  byte_size: number | null
  created_at: string
}

const ACCEPTED_MIME = 'image/jpeg,image/png,image/heic,image/webp,application/pdf'
const MAX_BYTES = 20 * 1024 * 1024 // 20 MB matches the bucket limit

export function DocumentsScreen() {
  const familyId = useAppStore((s) => s.familyId)
  const persons = useAppStore((s) => s.persons)
  const [uploadOpen, setUploadOpen] = useState(false)
  const qc = useQueryClient()

  const docsQ = useQuery({
    enabled: !!familyId,
    queryKey: ['documents', familyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('documents')
        .select('id, family_id, person_id, document_type, display_name, storage_status, storage_ref, mime_type, byte_size, created_at')
        .eq('family_id', familyId!)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as DocumentRow[]
    },
  })

  const packColor = PACK_COLORS.school.light

  return (
    <Screen
      title="Documents"
      action={
        <button
          type="button"
          onClick={() => setUploadOpen(true)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full text-text-secondary hover:bg-white/5"
          aria-label="Upload document"
        >
          <PlusIcon size={20} />
        </button>
      }
    >
      <div className="px-4 py-3 space-y-4">
        {docsQ.isLoading && <div className="py-8 text-center text-caption text-text-tertiary">Loading…</div>}
        {docsQ.isError && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
            {(docsQ.error as Error).message}
          </div>
        )}

        {docsQ.data && docsQ.data.length === 0 && (
          <div className="rounded-r-sm border border-divider-subtle bg-bg-elevated py-10 px-6 text-center">
            <SchoolIcon size={28} className="mx-auto text-text-quiet" />
            <div className="mt-3 font-serif italic text-title text-text-secondary">No documents yet.</div>
            <div className="mt-1 text-caption text-text-tertiary">
              Insurance cards, immunization records, prescriptions — anything the pediatrician / school / lawyer might ask for.
            </div>
            <div className="mt-4">
              <Button variant="primary" size="md" packColor={packColor} onClick={() => setUploadOpen(true)}>
                Upload first document
              </Button>
            </div>
          </div>
        )}

        {docsQ.data && docsQ.data.length > 0 && (
          <div className="rounded-r-sm bg-bg-elevated border border-divider-subtle divide-y divide-divider-subtle">
            {docsQ.data.map((d) => (
              <DocumentRowItem key={d.id} doc={d} persons={persons} onDelete={() => qc.invalidateQueries({ queryKey: ['documents'] })} />
            ))}
          </div>
        )}
      </div>

      <UploadSheet
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => qc.invalidateQueries({ queryKey: ['documents'] })}
      />
    </Screen>
  )
}

function DocumentRowItem({
  doc,
  persons,
  onDelete,
}: {
  doc: DocumentRow
  persons: Array<{ id: string; display_name: string }>
  onDelete: () => void
}) {
  const [busy, setBusy] = useState(false)
  const person = persons.find((p) => p.id === doc.person_id)

  async function download() {
    if (!doc.storage_ref) return
    setBusy(true)
    try {
      const { data, error } = await supabase.storage.from('documents').createSignedUrl(doc.storage_ref, 60)
      if (error) throw new Error(error.message)
      if (data.signedUrl) window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
    } catch (e) {
      window.alert(`Download failed: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!confirm(`Delete "${doc.display_name}"? This can't be undone.`)) return
    setBusy(true)
    try {
      if (doc.storage_ref) {
        const { error: storageErr } = await supabase.storage.from('documents').remove([doc.storage_ref])
        if (storageErr) throw new Error(storageErr.message)
      }
      const { error: rowErr } = await supabase.from('documents').delete().eq('id', doc.id)
      if (rowErr) throw new Error(rowErr.message)
      onDelete()
    } catch (e) {
      window.alert(`Delete failed: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(false)
    }
  }

  const subtitle = [
    prettyDocType(doc.document_type),
    person ? `for ${person.display_name}` : null,
    doc.byte_size ? formatBytes(doc.byte_size) : null,
    `added ${relativeTime(doc.created_at)}`,
  ].filter(Boolean).join(' · ')

  return (
    <ListRow
      title={doc.display_name}
      subtitle={subtitle}
      trailing={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={download}
            disabled={busy || !doc.storage_ref}
            className="text-caption text-text-secondary underline underline-offset-2 hover:text-text-primary disabled:opacity-50"
          >
            {busy ? '…' : 'Open'}
          </button>
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            className="text-caption text-pack-safety underline underline-offset-2 disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      }
    />
  )
}

function UploadSheet({
  open,
  onClose,
  onUploaded,
}: {
  open: boolean
  onClose: () => void
  onUploaded: () => void
}) {
  const familyId = useAppStore((s) => s.familyId)
  const user = useAppStore((s) => s.user)
  const persons = useAppStore((s) => s.persons)
  const fileInput = useRef<HTMLInputElement>(null)

  const [displayName, setDisplayName] = useState('')
  const [documentType, setDocumentType] = useState<DocumentType>('insurance')
  const [personId, setPersonId] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  function pickFile(f: File | null) {
    setErr(null)
    if (!f) { setFile(null); return }
    if (f.size > MAX_BYTES) {
      setErr(`File is too large (${formatBytes(f.size)}). Max 20 MB.`)
      setFile(null); return
    }
    setFile(f)
    if (!displayName.trim()) setDisplayName(cleanFileName(f.name))
  }

  function reset() {
    setDisplayName(''); setDocumentType('insurance'); setPersonId(''); setFile(null); setErr(null)
    if (fileInput.current) fileInput.current.value = ''
  }

  async function upload() {
    if (!familyId || !user) return
    setBusy(true); setErr(null)
    try {
      if (!file) throw new Error('Pick a file to upload')
      if (!displayName.trim()) throw new Error('Give the document a name')

      // Storage path: <family_id>/<uuid>-<safe_filename>
      // Family-id prefix is enforced by the RLS policy on storage.objects (is_active_family_member).
      const ext = extensionOf(file.name)
      const uuid = crypto.randomUUID()
      const path = `${familyId}/${uuid}${ext}`

      const { error: uploadErr } = await supabase.storage.from('documents').upload(path, file, {
        contentType: file.type || 'application/octet-stream',
        upsert: false,
      })
      if (uploadErr) throw new Error(`Upload failed: ${uploadErr.message}`)

      const { error: rowErr } = await supabase.from('documents').insert({
        family_id: familyId,
        person_id: personId || null,
        document_type: documentType,
        display_name: displayName.trim(),
        storage_status: 'uploaded',
        storage_backend: 'supabase',
        storage_ref: path,
        mime_type: file.type || null,
        byte_size: file.size,
        ingestion_source: 'manual_upload',
        extracted_data: {},
        created_by_user_id: user.id,
      })
      if (rowErr) {
        // Roll back the upload if row insert fails so we don't leak orphan objects
        await supabase.storage.from('documents').remove([path])
        throw new Error(`Failed to record document: ${rowErr.message}`)
      }

      onUploaded()
      reset()
      onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <BottomSheet
      open={open}
      onClose={() => { reset(); onClose() }}
      title="Upload document"
      action={
        <Button
          variant="primary"
          size="lg"
          packColor={PACK_COLORS.school.light}
          loading={busy}
          onClick={upload}
          className="w-full"
        >
          Upload
        </Button>
      }
    >
      <div className="space-y-4 pt-3">
        <div>
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPTED_MIME}
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
            className="block w-full text-caption text-text-secondary file:mr-3 file:rounded-full file:border-0 file:bg-bg-elevated file:px-4 file:py-2 file:text-caption file:font-medium file:text-text-primary hover:file:bg-bg-raised"
          />
          {file && (
            <div className="mt-2 text-caption text-text-tertiary">
              {file.name} · {formatBytes(file.size)} · {file.type || 'unknown type'}
            </div>
          )}
        </div>

        <Field label="Display name">
          <TextInput value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="What is this?" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <Select value={documentType} onChange={(e) => setDocumentType(e.target.value as DocumentType)}>
              {DOC_TYPES.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </Select>
          </Field>
          <Field label="Person (optional)">
            <Select value={personId} onChange={(e) => setPersonId(e.target.value)}>
              <option value="">Family (no specific person)</option>
              {persons.map((p) => (
                <option key={p.id} value={p.id}>{p.display_name}</option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="text-caption text-text-tertiary">
          Max 20 MB. Accepted: JPG / PNG / HEIC / WebP / PDF. Everything stays in your family — nobody outside sees it.
        </div>

        {err && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">{err}</div>
        )}
      </div>
    </BottomSheet>
  )
}

function prettyDocType(t: string): string {
  return DOC_TYPES.find((x) => x.key === t)?.label ?? t.replace(/_/g, ' ')
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function cleanFileName(name: string): string {
  const withoutExt = name.replace(/\.[a-z0-9]{2,5}$/i, '')
  return withoutExt.replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim()
}

function extensionOf(name: string): string {
  const m = name.match(/\.[a-z0-9]{2,5}$/i)
  return m ? m[0].toLowerCase() : ''
}
